import {
  ApiMethod,
  JSONObject,
  ReferenceControl,
} from '../../../../../../types/types';
import { FormApiMethodsRequestData } from '../../../../../../api/types/types';
import type { RequestDataValue } from '../../../../../../context/RequestDataContext';
import { createRequestBody } from '../../../../../../context/DatasetFormDataContext/utils/createRequestBody';
import { createApiLookupFunction } from '../../../../../../utils/json/customFunctions/createApiLookupFunction/createApiLookupFunction';
import { resolveStringPlaceholders } from '../../../../../../utils/json/resolveStringPlaceholders';
import { resolveTemplate } from '../../../../../../utils/json/resolveTemplate';
import { debugLogger } from '../../../../../../debug/DebugLogger';

const log = debugLogger.scope('filters');

// Mirrors createApiLookupFunction's local HandleApiError type — kept structural
// so callers don't have to import from the hook module.
type HandleApiError = (error: unknown) => void;

export type ResolveControlFilterParams = {
  // Raw filter from the form's ReferenceControl. May be undefined / object / string (JSONata).
  rawFilter: ReferenceControl['filter'];
  // Both absent in filter forms — there is no edit form around the reference,
  // so the JSONata context is the request data alone (no `form` key).
  formDefinition?: Record<string, any>;
  formValues?: Record<string, unknown>;
  requestData: RequestDataValue;
  handlerApiMethods: Record<string, ApiMethod>;
  // Forwarded into createApiLookupFunction so $apiLookup failures route through
  // the standard toast/session handler.
  handleApiError: HandleApiError;
};

export type ResolveControlFilterResult =
  | { ok: true; filter: Record<string, any> | undefined }
  | { ok: false; error: Error };

/**
 * Resolves a ReferenceControl filter through JSONata, supporting string filters
 * (whole expression) and object filters (embedded {{ }} placeholders). Binds
 * $apiLookup so filter expressions can call form apiMethods. Shared by inline
 * popover fetches and click-time pre-resolution for FullscreenReference, whose
 * route unmounts the form context and can't resolve filters itself.
 *
 * Works without a form too (reference inside a filter form): the context is
 * then the scope's request data only — datasetControl, filters, sort, … —
 * and `$apiLookup` is unavailable because apiMethods live on the form.
 */
export const resolveControlFilter = async ({
  rawFilter,
  formDefinition,
  formValues,
  requestData,
  handlerApiMethods,
  handleApiError,
}: ResolveControlFilterParams): Promise<ResolveControlFilterResult> => {
  // Sanitize form values to drop staticText/omitIfEmpty fields before they
  // become part of the JSONata baseContext.
  const sanitizedForm = formDefinition
    ? createRequestBody(formDefinition, formValues ?? {})
    : undefined;
  const baseContext = {
    ...requestData,
    ...(sanitizedForm ? { form: sanitizedForm } : {}),
  } as JSONObject;

  const { apiLookup, hasError } = createApiLookupFunction(
    handlerApiMethods,
    // Cast: only form scope supplies apiMethods, so a filter-scope context can
    // never reach the handler (every funcId throws on the empty pool first).
    baseContext as FormApiMethodsRequestData & JSONObject,
    handleApiError
  );

  let rawResolved: unknown;
  try {
    if (typeof rawFilter === 'string') {
      // Whole filter is a JSONata expression string — resolve to object/null.
      // Spread sanitizedForm at root so expressions can reference form fields
      // by name (e.g. `dodavatel`) without needing `form.dodavatel`.
      const filterContext = {
        ...baseContext,
        ...(sanitizedForm ?? {}),
      } as JSONObject;
      rawResolved = await resolveStringPlaceholders(rawFilter, filterContext, {
        apiLookup,
      });
    } else {
      // Object filter — recursively resolve any embedded {{ }} placeholders.
      rawResolved = await resolveTemplate(rawFilter, baseContext, {
        apiLookup,
      });
    }
  } catch (filterError) {
    /** @debugFeature filters Logs a reference control.filter whose expression failed to resolve — the fetch is aborted. */
    log('control.filter resolve failed', () => ({
      rawFilter,
      error: filterError,
    }));

    return {
      ok: false,
      error:
        filterError instanceof Error
          ? filterError
          : new Error('reference filter expression failed'),
    };
  }

  // Sticky flag — set if any $apiLookup hit an API error during resolution.
  if (hasError()) {
    /** @debugFeature filters Logs a reference control.filter whose $apiLookup hit an API error during resolution — the fetch is aborted. */
    log('control.filter $apiLookup failed', () => ({ rawFilter }));

    return {
      ok: false,
      error: new Error('reference filter $apiLookup failed'),
    };
  }

  /** @debugFeature filters Logs a resolved reference control.filter (locked filter): the raw filter and the result — a null result means the lock silently disappears. */
  log('control.filter resolved', () => ({
    rawFilter,
    resolvedFilter: rawResolved,
    lockDropped: rawResolved == null,
  }));

  // null result (e.g. conditional that evaluated falsy) means no filter.
  return {
    ok: true,
    filter:
      rawResolved != null ? (rawResolved as Record<string, any>) : undefined,
  };
};
